<template>
  <div class="get-away-summary-flat">
    <div>
      <div
        v-if="showTextSummary && hasValidAvailability"
        class="get-away-summary-flat__text-summary"
      >
        <CheckListItem
          v-if="servicesLabel"
          variant="secondary"
        >
          {{ servicesLabel }}
        </CheckListItem>

        <CheckListItem
          v-if="paxBookLabel"
          variant="secondary"
        >
          {{ paxBookLabel }}
        </CheckListItem>

        <div
          v-if="totalNightsLabel"
          class="get-away-summary-flat__text-summary__nights"
        >
          <CheckListItem variant="secondary">
            {{ totalNightsLabel }}
          </CheckListItem>
          <div>
            <span v-if="showPrice">
              {{ basePriceLabel }}
            </span>
          </div>
        </div>

        <CheckListItem
          v-if="occupanciesLabel"
          variant="secondary"
        >
          {{ occupanciesLabel }}
        </CheckListItem>

        <CheckListItem
          v-if="boardtypeLabel"
          variant="secondary"
        >
          {{ boardtypeLabel }}
        </CheckListItem>

        <CheckListItem
          v-if="isNotRefundable"
          variant="acent"
        >
          <Lang
            by-key="no-refundable"
            capitalize-first
          />.
          <Lang
            by-key="no-changes-nor-cancel-allowed"
            capitalize-first
          />
        </CheckListItem>

        <CheckListItem
          v-if="isRefundable"
          variant="acent"
        >
          <DynLang
            by-key="refunds-label-date"
            capitalize-first
            :params="{
              deadline: refundDeadlineDateStr,
              price: refundDeadlinePriceStr,
            }"
          />
        </CheckListItem>

        <div
          v-if="showDiscountLabel && discountLabel"
          class="get-away-summary-flat__text-summary__discount"
        >
          <CheckListItem acent>
            <Lang
              by-key="discount-for-time"
              capitalize-first
            />:
          </CheckListItem>
          <div>{{ discountLabel }}</div>
        </div>
      </div>

      <div
        v-if="showPrice && hasValidAvailability"
        class="get-away-summary-flat__price"
      >
        <GetAwayPrice
          v-if="rate"
          :rate="rate"
          left
        />
      </div>
    </div>

    <div v-if="showGetAway && getAway">
      <Card
        transparent
        class="get-away-summary-flat__get-away-details"
      >
        <div class="get-away-summary-flat__get-away-details__header">
          <div class="get-away-summary-flat__get-away-details__header__title">
            {{ getAwayName }}
          </div>

          <HotelNameStarsAtom
            inline-stars
            :hotel="getAway.hotel"
          />
        </div>

        <!-- Fechas y personas-->
        <div
          v-if="datesStrLabels"
          class="get-away-summary-flat__date-pax-boxes"
        >
          <div class="get-away-summary-flat__date-pax-boxes__dates">
            <div class="get-away-summary-flat__date-pax-boxes__box">
              {{ datesStrLabels.from }}
            </div>
            <div class="only-mobile">
              al
            </div>
            <div class="get-away-summary-flat__date-pax-boxes__box">
              {{ datesStrLabels.to }}
            </div>
          </div>

          <div
            v-if="paxPeopleLabel"
            class="get-away-summary-flat__date-pax-boxes__box"
          >
            {{ paxPeopleLabel }}
          </div>
        </div>

        <div class="get-away-summary-flat__text-refunds only-desktop-block">
          <CheckListItem
            v-if="isNotRefundable"
            variant="acent"
          >
            <Lang
              by-key="no-refundable"
              capitalize-first
            />.
            <Lang
              by-key="no-changes-nor-cancel-allowed"
              capitalize-first
            />
          </CheckListItem>

          <CheckListItem
            v-if="isRefundable"
            variant="acent"
          >
            <DynLang
              by-key="refunds-label-time"
              capitalize-first
              :params="{
                deadline: refundDeadlineDateWithTimeStr,
                price: refundDeadlinePriceStr,
              }"
            />.
          </CheckListItem>
        </div>
      </Card>
    </div>
  </div>
</template>

<script lang="ts">
import CheckListItem from '../shared/CheckListItem.vue'
import GetAwayPrice from './GetAwayPrice.vue'
import HotelNameStarsAtom from '../hotel/atom/HotelNameStars.atom.vue'
import Card from '../shared/Card.vue'

import { useGetAwayAvailability } from '~/core/composable/get-away/useGetAwayAvailability'

import { GetAwayRate, type GetAwayAvailability } from '~/core/ts/entity/GetAwayAvailability'
import { dateFromSecodsTimeStamp, dateToDDMMYYY, dateToDDMMYYYWithTime } from '~/core/ts/util/date'
import { capitalizeAllWords, capitalizeFirstWord } from '~/core/ts/util/string'
import { translate, translateByKey } from '~/core/ts/util/translate'

import type { GetAway } from '~/core/ts/entity/GetAway'
import { Money } from '~/core/ts/entity/Money'

export default defineNuxtComponent({
  components: {
    CheckListItem,
    GetAwayPrice,
    HotelNameStarsAtom,
    Card,
  },
  props: {
    availability: {
      type: Object as PropType<GetAwayAvailability | null>,
      default: () => null,
    },
    rate: {
      type: Object as PropType<GetAwayRate | null>,
      default: () => null,
    },
    getAway: {
      type: Object as PropType<GetAway | null>,
      default: () => null,
    },
    showTextSummary: {
      type: Boolean,
      default: false,
    },
    showDiscountLabel: {
      type: Boolean,
      default: false,
    },
    showPrice: {
      type: Boolean,
      default: false,
    },
    showGetAway: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const {
      isValidAvailability,
    } = useGetAwayAvailability()

    const hasValidAvailability = computed(() => {
      return isValidAvailability(props.availability)
    })

    const stayLabel = capitalizeFirstWord(translateByKey('stay'))
    const noSpaLabel = capitalizeFirstWord(translateByKey('no-spa'))
    const servicesLabel = computed(() => {
      if (!hasValidAvailability.value) {
        return ''
      }

      const getAway = props.getAway
      if (!getAway) {
        return ''
      }

      if (!props.rate) {
        return ''
      }

      const labelParts: Array<string> = []

      if (props.getAway.includesSpa) {
        labelParts.push(`${stayLabel} (${props.rate.name})`)
      } else {
        labelParts.push(`${stayLabel} (${props.rate.name}) (${noSpaLabel})`)
      }

      props.getAway.spa.services
        .forEach((service) => {
          labelParts.push(translate(service.texts).name)
        })

      return labelParts.join(' + ')
    })

    const bookingForLabel = capitalizeFirstWord(translateByKey('booking-for'))
    const paxBookLabel = computed(() => {
      if (!hasValidAvailability.value) {
        return ''
      }

      const avail = props.availability as GetAwayAvailability

      return bookingForLabel.replaceAll('{{label}}', avail.numPax.toString())
    })

    const nightLabel1 = capitalizeFirstWord(translateByKey('night', false))
    const nightLabelN = capitalizeFirstWord(translateByKey('night', true))
    const totalNightsLabel = computed(() => {
      if (!hasValidAvailability.value) {
        return ''
      }

      const avail = props.availability as GetAwayAvailability

      const nights = avail.totalNights


      return nights === 1 ? `1 ${nightLabel1}` : `${nights} ${nightLabelN}`
    })

    const basePriceLabel = computed(() => {
      if (!hasValidAvailability.value) {
        return ''
      }

      if (!props.rate) {
        return ''
      }

      return Money.toString(props.rate.basePrice)
    })

    const peopleLabel = translateByKey('person', true)
    const occupanciesLabel = computed(() => {
      if (!hasValidAvailability.value) {
        return ''
      }

      if (!props.rate) {
        return ''
      }

      const avail = props.availability as GetAwayAvailability

      const occupancies = props.rate.metadata.occupancies
      const roomName = props.rate.room.name
      const pax = avail.numPax

      return capitalizeAllWords(`${occupancies} × ${roomName} (${pax} ${peopleLabel})`)
    })

    const boardtypeLabel = computed(() => {
      if (!hasValidAvailability.value) {
        return ''
      }

      if (!props.rate) {
        return ''
      }

      const availability = props.availability as GetAwayAvailability
      const rate = props.rate

      const boardtype = availability.boardtypesAvailable
        .find((boardtype) => boardtype.type === rate.boardtype.type)

      if (!boardtype) {
        return ''
      }

      return capitalizeAllWords(boardtype.name)
    })

    const isRefundable = computed(() => {
      if (!props.rate) {
        return false
      }

      return GetAwayRate.isRefundable(props.rate)
    })

    const isNotRefundable = computed(() => {
      if (!props.rate) {
        return false
      }

      return !GetAwayRate.isRefundable(props.rate)
    })

    const refundDeadlineDateStr = computed(() => {
      if (!props.rate) {
        return ''
      }

      const date = GetAwayRate.getDeadlineDate(props.rate)
      if (!date) {
        return ''
      }

      return dateToDDMMYYY(date)
    })

    const refundDeadlineDateWithTimeStr = computed(() => {
      if (!props.rate) {
        return ''
      }

      const date = GetAwayRate.getDeadlineDate(props.rate)
      if (!date) {
        return ''
      }

      return dateToDDMMYYYWithTime(date)
    })

    const refundDeadlinePriceStr = computed(() => {
      if (!props.rate) {
        return ''
      }

      const price = GetAwayRate.getDeadlinePrice(props.rate)
      if (!price) {
        return ''
      }

      return Money.toString(price, 2)
    })

    const discountLabel = computed(() => {
      if (!props.rate) {
        return ''
      }

      const baseAmount = props.rate.basePrice.amount
      const discAmount = props.rate.basePriceWithDiscount.amount

      if (baseAmount === discAmount) {
        return ''
      }

      const diff = Money.fromAmount(baseAmount - discAmount)

      return ` -${Money.toString(diff, 2)}`
    })

    const datesStrLabels = computed(() => {
      if (!props.availability) {
        return null
      }

      const fDate = dateFromSecodsTimeStamp(props.availability.from)
      const tDate = dateFromSecodsTimeStamp(props.availability.to)

      const from = dateToDDMMYYY(fDate)
      const to = dateToDDMMYYY(tDate)

      return {
        from,
        to
      }
    })

    const personTxt = capitalizeAllWords(translateByKey('person', true))
    const paxPeopleLabel = computed(() => {
      if (!props.availability) {
        return ''
      }

      return `${props.availability.numPax} ${personTxt}`
    })

    // Textos
    const getAwayName = computed(() => {
      if (!props.getAway) {
        return ''
      }

      return translate(props.getAway.texts).name
    })

    return {
      servicesLabel,
      hasValidAvailability,

      getAwayName,

      paxBookLabel,
      totalNightsLabel,
      basePriceLabel,
      occupanciesLabel,
      boardtypeLabel,

      datesStrLabels,
      paxPeopleLabel,

      isRefundable,
      isNotRefundable,
      refundDeadlineDateStr,
      refundDeadlineDateWithTimeStr,
      refundDeadlinePriceStr,

      discountLabel,
    }
  }
})
</script>

<style lang="scss" scoped>
.get-away-summary-flat {
  @apply flex;
  @apply flex-col-reverse;

  @apply p-2;

  @screen lg {
    @apply p-0;
    @apply flex-col;
  }

  &__text-summary {
    @apply text-spl-dark;
    @apply font-semibold;

    &__nights, &__discount {
      @apply flex;
      @apply items-center;
      @apply justify-between;
    }

    &__discount {
      @apply mt-2;
      @apply text-spl-secondary-dark;

      @screen lg {
        @apply mt-4;
      }
    }
  }

  &__text-refunds {
    @apply text-spl-dark;
  }

  &__price {
    @apply py-4;
    @apply px-4;
    @apply my-2;

    @apply border-dotted border-t-2;

    @apply border-spl-gray-3;
    @screen lg {
      @apply border-spl-gray-2;
    }
  }

  &__get-away-details {
    @apply py-6;
    @apply px-4;

    &__header{
      @apply mb-2;

      &__title {
        @apply text-spl-dark;
        @apply font-bold;

        @apply text-2xl;
      }
    }
  }

  &__date-pax-boxes {
    @apply my-4;
    @screen lg {
      @apply my-8;
    }

    &__dates {
      @apply w-full;

      @apply flex;
      @apply items-center;
      @apply justify-center;

      @apply gap-4;
      @apply mb-2;

      @screen lg {
        @apply gap-2;
        @apply justify-stretch;
      }
    }

    &__box {
      @apply text-spl-dark;
      @apply text-lg;

      @screen lg {
        @apply w-full;
        @apply p-2;

        @apply border-1;
        @apply rounded-sm;

        @apply text-spl-gray-4;
      }

      @apply text-center;
      @apply font-semibold;
    }
  }
}
</style>
